import { List, Text, TextSkeleton } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { useGetWorkplacesById } from "@src/appV2/redesign/Workplace/useGetWorkplacesById";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { minutesToMilliseconds } from "date-fns";

import { useCredentialRequirementConfiguration } from "../../useCredentialRequirementConfiguration";
import { getApplicableWorkplacesDetails } from "../../utils/getApplicableWorkplacesDetails";
import { getApplicableWorkplacesTextForRequirement } from "../../utils/getApplicableWorkplacesTextForRequirement";
import { getRequirementWorkplacesIds } from "../getRequirementWorkplacesIds";
import { useRequirementDetailsContext } from "../useRequirementDetailsContext";
import { DocumentDetailsRequiredByWorkplacesListItem } from "./ListItem";

export function DocumentDetailsRequiredBySection() {
  const { requirement } = useRequirementDetailsContext();
  const { requirementId } = requirement;
  const worker = useDefinedWorker();

  const requiredWorkplaceIds = getRequirementWorkplacesIds(requirement);
  const applicableWorkplacesDetails = getApplicableWorkplacesDetails({
    workerRequirement: requirement,
    workerAddress: worker.address,
    requiredByWorkplacesCount: requiredWorkplaceIds.length,
  });

  const workplacesResponse = useGetWorkplacesById(
    { workplaceIds: requiredWorkplaceIds },
    {
      enabled: isDefined(applicableWorkplacesDetails) && requiredWorkplaceIds.length === 1,
      staleTime: minutesToMilliseconds(5),
    }
  );

  const credentialRequirementConfiguration = useCredentialRequirementConfiguration();
  const isCredentialRequirement = credentialRequirementConfiguration.has(requirementId);

  const { data: workplaces, isLoading, fetchStatus, isError } = workplacesResponse;

  const isSingleWorkplace = requiredWorkplaceIds.length === 1;

  function getDisplayText() {
    if (applicableWorkplacesDetails?.type === "WORKPLACE" && isSingleWorkplace) {
      return workplaces?.pages?.[0]?.data?.[0]?.attributes?.name;
    }

    if (!isDefined(applicableWorkplacesDetails)) {
      return undefined;
    }

    return getApplicableWorkplacesTextForRequirement(applicableWorkplacesDetails, false);
  }

  const displayText = getDisplayText();

  if (isLoading && fetchStatus !== "idle") {
    return (
      <Stack justifyContent="center" minHeight={64}>
        <TextSkeleton variant="h4" width="60%" />
      </Stack>
    );
  }

  if (
    isCredentialRequirement ||
    !isDefined(displayText) ||
    !isDefined(applicableWorkplacesDetails) ||
    isError
  ) {
    return null;
  }

  return (
    <Stack spacing={1}>
      <Text semibold variant="h5" sx={(theme) => ({ color: theme.palette.text.secondary })}>
        Required by:
      </Text>

      <List dense>
        <DocumentDetailsRequiredByWorkplacesListItem
          text={displayText}
          type={applicableWorkplacesDetails.type}
          requiredWorkplaceIds={requiredWorkplaceIds}
          requirementId={requirementId}
        />
      </List>
    </Stack>
  );
}
